/* eslint-disable @typescript-eslint/indent */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Params } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { Asset } from "../assets/asset.type";
import { Quote } from "./quote.type";
import { BulkUpdateQuotedAssetDto, QuotedAsset } from "./quote.type";

@Injectable({
  providedIn: "root",
})
export class QuoteService {
  path = "/quotes";
  basePath = `${environment.serverUrl}/quotes`;
  basePathQuotedAssets = `${environment.serverUrl}/quoted-assets`;
  basePathCsvQueue = `${environment.serverUrl}/quotes/queue-csv`;

  constructor(private http: HttpClient) {}

  createQuote(data): Observable<Quote> {
    return this.http.post<Quote>(this.basePath, data).pipe(
      tap((_data: Quote) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getQuotes(options?: QueryParams<Quote>): Observable<PaginatedResponse<Quote>> {
    this.parseFilters(options);
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<PaginatedResponse<Quote>>(this.basePath, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getQuotedAssets(options?: QueryParams<QuotedAsset>): Observable<PaginatedResponse<QuotedAsset>> {
    this.parseFilters(options);
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<PaginatedResponse<QuotedAsset>>(this.basePathQuotedAssets, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getQuotesQueue(options?: QueryParams<Quote>): Observable<void> {
    this.parseFilters(options);
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getQuote(quoteId: string, options?: QueryParams<Quote>): Observable<Quote> {
    let params: HttpParams = new HttpParams();
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<Quote>(`${this.basePath}/${quoteId}`, { params }).pipe(
      tap((_data: Quote) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateQuote(quoteId: string, data: Partial<Quote>): Observable<Quote> {
    return this.http.put<Quote>(`${this.basePath}/${quoteId}`, data).pipe(
      tap((_data: Quote) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  linkAssets(quoteId: string, body): Observable<void> {
    return this.http.put<void>(`${this.basePath}/${quoteId}/assets`, body).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateQuotedAsset(quoteId: string, body): Observable<Asset> {
    return this.http.put<void>(`${this.basePathQuotedAssets}/${quoteId}`, body).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  bulkUpdateQuotedAsset(body: BulkUpdateQuotedAssetDto): Observable<Asset> {
    return this.http.put<void>(`${this.basePathQuotedAssets}/bulk-update`, body).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  convertQuoteToContract(id: string): Observable<Quote> {
    return this.http.post<Quote>(`${this.basePath}/${id}/convert`, {}).pipe(
      tap((_data: Quote) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  /** Parse/convert the values in the "where" property */
  private parseFilters(options?: Params): void {
    // When the filter (search filter) is "number" we have to handle the "-" to avoid issues on the search
    if (options?.where?.number?.$cont && options.where.number.$cont?.toString()?.includes("-")) {
      options.where.number.$cont = options.where.number.$cont.split("-")[0];
    }
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return throwError(error);
    };
  }
}
